import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Ticket,
  Calendar,
  User,
  ChevronRight,
  ChevronLeft,
  CreditCard,
  Users,
  ShieldCheck,
  AlertCircle,
  Download,
  ImageIcon,
  Smartphone,
  MousePointerClick,
  Copy,
  CheckCircle2,
  Tag,
  X,
  ArrowRight,
  ShoppingBag,
  TrendingUp,
  Wallet,
  QrCode,
  Loader2,
} from "lucide-react";
import {
  BookingState,
  MobileActionState,
  TicketOption,
  BookingRecord,
  Voucher,
} from "../types";
import {
  getTickets,
  getServiceFeeInfo,
  ServiceFeeInfo,
} from "../services/api/tickets";
import {
  createBooking,
  validateVoucher,
  getBookingByCode,
} from "../services/api/bookings";
import {
  subscribeToBookingStatus,
  BookingStatusData,
} from "../services/api/realtime";
import { PaymentGateway } from "./PaymentGateway";
import { formatIDR } from "../services/utils";
import Swal from "sweetalert2";

interface BookingWidgetProps {
  setMobileAction?: (action: MobileActionState | null) => void;
  activeCategory?: "personal" | "rombongan" | null;
  onActivate?: () => void;
  onSwitchCategory?: (category: "personal" | "rombongan") => void;
}

export const BookingWidget: React.FC<BookingWidgetProps> = ({
  setMobileAction,
  activeCategory,
  onActivate,
  onSwitchCategory,
}) => {
  // Load tickets from API
  const [tickets, setTickets] = useState<TicketOption[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceFeeInfo, setServiceFeeInfo] = useState<ServiceFeeInfo | null>(
    null
  );

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setIsLoadingTickets(true);
        const [ticketsData, feeInfo] = await Promise.all([
          getTickets(),
          getServiceFeeInfo(),
        ]);
        setTickets(ticketsData);
        setServiceFeeInfo(feeInfo);
      } catch (error) {
        console.error("Failed to fetch tickets:", error);
      } finally {
        setIsLoadingTickets(false);
      }
    };
    fetchTickets();
  }, []);

  const [booking, setBooking] = useState<BookingState>({
    step: 1,
    selectedDate: new Date().toISOString().split("T")[0],
    selectedTicket: null,
    quantity: 1,
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    paymentMethod: "loket",
  });
  const [confirmEmail, setConfirmEmail] = useState("");
  const [bookingCode, setBookingCode] = useState("");
  const [createdBooking, setCreatedBooking] = useState<BookingRecord | null>(
    null
  );
  const [realtimeStatus, setRealtimeStatus] =
    useState<BookingStatusData | null>(null);

  const [isCopied, setIsCopied] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Voucher State
  const [voucherCodeInput, setVoucherCodeInput] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState<{
    code: string;
    discountType: string;
    discountValue: number;
    partnerName: string | null;
  } | null>(null);
  const [voucherMessage, setVoucherMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isValidatingVoucher, setIsValidatingVoucher] = useState(false);
  const [pendingVoucherFromUrl, setPendingVoucherFromUrl] = useState<
    string | null
  >(null);

  // Check URL for voucher parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const voucherFromUrl = urlParams.get("voucher");
    if (voucherFromUrl) {
      setVoucherCodeInput(voucherFromUrl);
      setPendingVoucherFromUrl(voucherFromUrl);
    }
  }, []);

  const selectedTicketDetails = useMemo(
    () => tickets.find((t) => t.id === booking.selectedTicket),
    [booking.selectedTicket, tickets]
  );

  const priceCalculation = useMemo(() => {
    if (!selectedTicketDetails)
      return {
        subTotal: 0,
        discount: 0,
        serviceFee: 0,
        total: 0,
        isInFreePeriod: true,
      };

    const subTotal = selectedTicketDetails.price * booking.quantity;
    let discount = 0;

    if (appliedVoucher) {
      if (appliedVoucher.discountType === "percent") {
        discount = subTotal * (appliedVoucher.discountValue / 100);
      } else {
        discount = appliedVoucher.discountValue * booking.quantity;
        if (discount > subTotal) discount = subTotal;
      }
    }

    const subtotalAfterDiscount = subTotal - discount;

    // Service fee hanya ditambahkan jika sudah lewat free period
    const isInFreePeriod = serviceFeeInfo?.isInFreePeriod ?? true;
    const serviceFeePercent = serviceFeeInfo?.serviceFeePercent ?? 0;
    const serviceFee = isInFreePeriod
      ? 0
      : Math.floor(subtotalAfterDiscount * (serviceFeePercent / 100));

    return {
      subTotal,
      discount,
      serviceFee,
      serviceFeePercent,
      isInFreePeriod,
      total: Math.max(0, subtotalAfterDiscount + serviceFee),
    };
  }, [selectedTicketDetails, booking.quantity, appliedVoucher, serviceFeeInfo]);

  const handleApplyVoucher = async () => {
    if (!voucherCodeInput.trim() || !booking.selectedTicket) return;

    setIsValidatingVoucher(true);
    try {
      const result = await validateVoucher(
        voucherCodeInput.trim(),
        booking.selectedTicket
      );

      if (result) {
        setAppliedVoucher({
          code: result.code,
          discountType: result.discountType,
          discountValue: result.discountValue,
          partnerName: result.partnerName,
        });
        setVoucherMessage({
          type: "success",
          text: result.partnerName
            ? `Voucher ${result.code} dari ${result.partnerName} berhasil diterapkan!`
            : `Voucher ${result.code} berhasil diterapkan!`,
        });
      } else {
        setAppliedVoucher(null);
        setVoucherMessage({
          type: "error",
          text: "Kode voucher tidak ditemukan atau tidak valid.",
        });
      }
    } catch (error) {
      setAppliedVoucher(null);
      setVoucherMessage({
        type: "error",
        text: "Gagal memvalidasi voucher. Coba lagi.",
      });
    } finally {
      setIsValidatingVoucher(false);
    }
  };

  const removeVoucher = () => {
    setAppliedVoucher(null);
    setVoucherCodeInput("");
    setVoucherMessage(null);
  };

  // Auto-apply voucher from URL when ticket is selected
  useEffect(() => {
    if (pendingVoucherFromUrl && booking.selectedTicket && !appliedVoucher) {
      const autoApplyVoucher = async () => {
        setIsValidatingVoucher(true);
        try {
          const result = await validateVoucher(
            pendingVoucherFromUrl,
            booking.selectedTicket!
          );

          if (result) {
            setAppliedVoucher({
              code: result.code,
              discountType: result.discountType,
              discountValue: result.discountValue,
              partnerName: result.partnerName,
            });
            setVoucherMessage({
              type: "success",
              text: result.partnerName
                ? `Voucher ${result.code} dari ${result.partnerName} berhasil diterapkan!`
                : `Voucher ${result.code} berhasil diterapkan!`,
            });
          } else {
            setVoucherMessage({
              type: "error",
              text: "Kode voucher tidak ditemukan atau tidak valid.",
            });
          }
        } catch {
          setVoucherMessage({
            type: "error",
            text: "Gagal memvalidasi voucher.",
          });
        } finally {
          setIsValidatingVoucher(false);
          setPendingVoucherFromUrl(null);
        }
      };
      autoApplyVoucher();
    }
  }, [pendingVoucherFromUrl, booking.selectedTicket, appliedVoucher]);

  // Validation logic
  const isNextDisabled = useMemo(() => {
    const isPhoneValid = /^628\d{7,17}$/.test(booking.customerPhone);
    if (booking.step === 1) return !booking.selectedTicket;
    if (booking.step === 2) {
      return (
        !booking.customerName ||
        !booking.customerEmail ||
        !booking.customerEmail.includes("@") ||
        booking.customerEmail !== confirmEmail ||
        !booking.customerPhone ||
        !isPhoneValid
      );
    }
    return false;
  }, [booking, confirmEmail]);

  const handlePhoneChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    if (!digitsOnly) {
      setBooking((prev) => ({ ...prev, customerPhone: "" }));
      return;
    }

    let normalized = digitsOnly;

    if (normalized.startsWith("0")) {
      normalized = "62" + normalized.slice(1);
    }

    if (!normalized.startsWith("62")) {
      normalized = "62" + normalized;
    }

    if (!normalized.startsWith("628")) {
      normalized = "628" + normalized.slice(2);
    }

    const trimmed = normalized.slice(0, 19);
    setBooking((prev) => ({ ...prev, customerPhone: trimmed }));
  };

  const handleNext = async () => {
    if (booking.step === 2) {
      if (!isAccepted) return;
      setIsSubmitting(true);
      try {
        const result = await createBooking({
          ticketId: booking.selectedTicket!,
          participant: booking.quantity,
          visitDate: booking.selectedDate,
          customerName: booking.customerName,
          customerEmail: booking.customerEmail,
          customerPhone: booking.customerPhone,
          voucherCode: appliedVoucher?.code,
          paymentMethod: booking.paymentMethod,
        });

        setBookingCode(result.bookingCode);

        const bookingRecord: BookingRecord = {
          id: result.bookingCode,
          code: result.bookingCode,
          customerName: result.customer.name,
          email: result.customer.email,
          phone: booking.customerPhone,
          date: result.visitDate,
          category: "Personal",
          ticketId: booking.selectedTicket!,
          ticketType: result.ticket.name,
          pax: result.participant,
          totalPrice: result.totalAmount,
          status: "PENDING",
          paymentMethod: booking.paymentMethod === "loket" ? "LOKET" : "QRIS",
          bookingDate: new Date().toISOString(),
          voucherCode: appliedVoucher?.code,
        };
        setCreatedBooking(bookingRecord);
        setBooking((prev) => ({ ...prev, step: prev.step + 1 }));
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Gagal Membuat Pesanan",
          text: "Terjadi kesalahan saat memproses pesanan Anda. Silakan coba lagi.",
          confirmButtonColor: "#4F46E5",
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setBooking((prev) => ({ ...prev, step: prev.step + 1 }));
    }
  };

  const handleBack = () =>
    setBooking((prev) => ({ ...prev, step: prev.step - 1 }));

  const handleCopyCode = () => {
    if (bookingCode) {
      navigator.clipboard.writeText(bookingCode);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handlePaymentSuccess = async () => {
    if (createdBooking) {
      try {
        const updated = await getBookingByCode(createdBooking.code);
        if (updated) setCreatedBooking(updated);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Gagal Memperbarui Status Pesanan",
          text: "Terjadi kesalahan saat memperbarui status pesanan Anda. Silakan coba lagi.",
          confirmButtonColor: "#4F46E5",
        });
      }
    }
  };

  const resetForm = () => {
    setBooking({
      step: 1,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedTicket: null,
      quantity: 1,
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      paymentMethod: "loket",
    });
    setConfirmEmail("");
    setBookingCode("");
    setCreatedBooking(null);
    setRealtimeStatus(null);
    removeVoucher();
    setIsAccepted(false);
  };

  useEffect(() => {
    if (!bookingCode || booking.step !== 3) return;

    const unsubscribe = subscribeToBookingStatus(bookingCode, (data) => {
      setRealtimeStatus(data);

      if (data.status === "completed" || data.status === "paid") {
        if (createdBooking) {
          setCreatedBooking({
            ...createdBooking,
            status: data.status.toUpperCase() as BookingRecord["status"],
          });
        }

        if (booking.paymentMethod === "loket" && data.status === "completed") {
          Swal.fire({
            icon: "success",
            title: "Pembayaran Dikonfirmasi!",
            text: "Tiket Anda sudah aktif. Terima kasih telah berkunjung!",
            showConfirmButton: false,
            timer: 2000,
          });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [bookingCode, booking.step, booking.paymentMethod]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (!entry.isIntersecting && setMobileAction) {
          setMobileAction(null);
        }
      },
      { threshold: 0 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [setMobileAction]);

  useEffect(() => {
    if (!isVisible || !setMobileAction) return;

    let label = "Pilih Tiket Dulu";
    let action: () => void | Promise<void> = handleNext;
    let disabled = isNextDisabled;

    if (booking.step === 1) {
      if (booking.selectedTicket) {
        label = "Lanjut Isi Data";
        disabled = false;
      } else {
        label = "Pilih Tiket Dulu";
        disabled = true;
      }
    } else if (booking.step === 2) {
      label = "Konfirmasi & Bayar";
      disabled = isNextDisabled || !isAccepted;
    } else if (booking.step === 3) {
      const isPending = createdBooking?.status === "PENDING";
      const isLoket = booking.paymentMethod === "loket";

      if (isPending && !isLoket) {
        label = "Selesaikan Bayar";
        action = () => {};
        disabled = true;
      } else {
        label = "Beli Tiket Lainnya";
        action = resetForm;
        disabled = false;
      }
    }

    setMobileAction({ label, onClick: action, disabled, priority: 2 });
  }, [
    isVisible,
    booking,
    isNextDisabled,
    isAccepted,
    setMobileAction,
    createdBooking,
  ]);

  const scrollToPartner = () => {
    if (onSwitchCategory) onSwitchCategory("rombongan");
    const section = document.getElementById("partner");
    if (section) section.scrollIntoView({ behavior: "smooth" });
  };

  const renderStep1_Ticket = () => (
    <div className="animate-fadeIn">
      <div className="flex justify-between items-center mb-6 md:mb-8">
        <h2 className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] md:tracking-[0.25em] text-slate-800 flex items-center gap-2">
          <ShoppingBag className="w-3.5 h-3.5 text-giz-primary" /> 01. Pilihan
          Tiket
        </h2>
        <span className="text-[8px] md:text-[9px] font-black text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full uppercase tracking-widest">
          Personal
        </span>
      </div>

      <div className="space-y-2 md:space-y-2 mb-2" role="radiogroup">
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            onClick={() => {
              setBooking((prev) => ({ ...prev, selectedTicket: ticket.id }));
              if (onActivate) onActivate();
            }}
            className={`cursor-pointer border-2 rounded-[1rem] md:rounded-[1rem] p-4 md:p-2 transition-all duration-500 relative overflow-hidden active:scale-[0.98] select-none ${
              booking.selectedTicket === ticket.id
                ? "border-giz-primary bg-indigo-50/40 shadow-xl"
                : "border-slate-100 bg-white hover:border-slate-200"
            }`}
          >
            <div className="flex justify-between items-start relative z-10 pointer-events-none">
              <div className="flex-1 pr-10">
                <div className="flex items-center gap-2 mb-1">
                  <h3
                    className={`font-black text-[10px] md:text-[11px] uppercase tracking-tight ${
                      booking.selectedTicket === ticket.id
                        ? "text-indigo-900"
                        : "text-slate-800"
                    }`}
                  >
                    {ticket.title}
                  </h3>
                  {ticket.recommended && (
                    <span className="bg-emerald-500 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ml-1 animate-pulse">
                      Recommended
                    </span>
                  )}
                  <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full ml-1 uppercase ${ticket.type === "fast-track" ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-600"}`}>
                    {ticket.type === "fast-track" ? "Fast Track" : "Regular"}
                  </span>
                </div>
                <p className="text-[8px] md:text-[9px] text-slate-400 font-bold uppercase tracking-wide line-clamp-1">
                  {ticket.description}
                </p>
              </div>
              <div
                className={`text-right ${
                  booking.selectedTicket === ticket.id ? "pr-8" : ""
                }`}
              >
                <span
                  className={`block text-md md:text-md font-black tracking-tight ${
                    booking.selectedTicket === ticket.id
                      ? "text-giz-primary"
                      : "text-slate-900"
                  }`}
                >
                  {formatIDR(ticket.price)}
                </span>
              </div>
            </div>
            {booking.selectedTicket === ticket.id && (
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 bg-giz-primary text-white p-1 rounded-full shadow-lg z-20 animate-scaleIn">
                <CheckCircle2 className="w-2.5 h-2.5" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="py-1 flex justify-center mb-4">
        <button
          onClick={(e) => {
            e.stopPropagation();
            scrollToPartner();
          }}
          className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-giz-primary transition-all flex items-center gap-2.5 group bg-slate-50 px-6 py-2.5 rounded-full border border-slate-100 shadow-sm"
        >
          <Users className="w-3.5 h-3.5 text-indigo-400" /> Bawa Rombongan (Min.
          30 Pax)?{" "}
          <ChevronRight className="w-3 h-3 group-hover:translate-x-1.5 transition-transform" />
        </button>
      </div>

      <div
        className={`p-2 md:p-2 bg-slate-50/50 rounded-[2rem] border border-slate-100 space-y-5 transition-all duration-700 ${
          !booking.selectedTicket
            ? "opacity-30 pointer-events-none"
            : "opacity-100"
        }`}
      >
        <div className="grid grid-cols-2 gap-4 md:gap-4">
          <div className="space-y-2">
            <label className="block text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1.5">
              Tanggal
            </label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="date"
                className="w-full bg-white border-2 border-slate-100 rounded-2xl p-3 pl-11 font-black text-slate-800 outline-none text-xs md:text-sm focus:border-giz-primary transition-all shadow-inner h-[48px] md:h-[56px]"
                value={booking.selectedDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setBooking((prev) => ({
                    ...prev,
                    selectedDate: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1.5">
              Pengunjung
            </label>
            <div className="flex items-center justify-between bg-white border-2 border-slate-100 rounded-2xl p-1 h-[48px] md:h-[56px] shadow-inner">
              <button
                onClick={() =>
                  setBooking((prev) => ({
                    ...prev,
                    quantity: Math.max(1, prev.quantity - 1),
                  }))
                }
                className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-slate-100 text-slate-900 flex items-center justify-center text-xl font-black hover:bg-slate-200 active:scale-95 transition-all"
              >
                -
              </button>
              <div className="flex flex-col items-center">
                <input
                  type="number"
                  min="1"
                  className="w-11 text-center bg-transparent border-none outline-none font-black text-slate-900 text-sm md:text-lg p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={booking.quantity}
                  onChange={(e) =>
                    setBooking((prev) => ({
                      ...prev,
                      quantity: Math.max(1, parseInt(e.target.value) || 1),
                    }))
                  }
                />
                <span className="text-[7px] md:text-[8px] font-black text-indigo-400 uppercase tracking-widest leading-none">
                  Pax
                </span>
              </div>
              <button
                onClick={() =>
                  setBooking((prev) => ({
                    ...prev,
                    quantity: prev.quantity + 1,
                  }))
                }
                className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-giz-primary text-white flex items-center justify-center text-xl font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 active:scale-95 transition-all"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {booking.selectedTicket && (
          <div className="bg-white border-2 border-indigo-100 p-3.5 rounded-2xl animate-slideUp space-y-2.5">
            <div className="flex justify-between items-center border-b border-slate-50 pb-1.5">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3 text-indigo-500" /> Akumulasi
              </span>
              <span className="text-xs font-black text-indigo-600">
                {booking.quantity} Tiket
              </span>
            </div>

            {/* Service Fee Info - hanya tampil jika lewat free period */}
            {!priceCalculation.isInFreePeriod &&
              priceCalculation.serviceFee > 0 && (
                <div className="flex justify-between items-center text-[10px] border-b border-slate-50 pb-1.5">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-bold text-slate-600">
                    {formatIDR(
                      priceCalculation.subTotal - priceCalculation.discount
                    )}
                  </span>
                </div>
              )}
            {!priceCalculation.isInFreePeriod &&
              priceCalculation.serviceFee > 0 && (
                <div className="flex justify-between items-center text-[10px] border-b border-slate-50 pb-1.5">
                  <span className="text-slate-500 flex items-center gap-1">
                    Biaya Layanan ({priceCalculation.serviceFeePercent}%)
                    <span
                      className="inline-block w-3 h-3 bg-slate-200 rounded-full text-[7px] text-center leading-3 cursor-help"
                      title="Biaya layanan platform untuk pemrosesan tiket digital"
                    >
                      ?
                    </span>
                  </span>
                  <span className="font-bold text-slate-600">
                    {formatIDR(priceCalculation.serviceFee)}
                  </span>
                </div>
              )}

            <div className="flex justify-between items-end">
              <div className="space-y-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Wallet className="w-3 h-3 text-emerald-500" /> Total Bayar
                </span>
                <p className="text-lg md:text-xl font-black text-slate-900 tracking-tighter">
                  {formatIDR(priceCalculation.total)}
                </p>
              </div>
              {priceCalculation.discount > 0 && (
                <span className="bg-emerald-500 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter animate-pulse">
                  Hemat {formatIDR(priceCalculation.discount)}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <label className="block text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
            Punya Kode Voucher?
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Contoh: GIZOCHI"
                className={`w-full bg-white border-2 rounded-xl p-2.5 pl-10 font-black text-slate-800 outline-none text-[11px] md:text-sm transition-all h-[42px] md:h-[48px] uppercase tracking-widest ${
                  appliedVoucher
                    ? "border-emerald-200 bg-emerald-50/20"
                    : "border-slate-100 focus:border-giz-primary"
                }`}
                value={appliedVoucher ? appliedVoucher.code : voucherCodeInput}
                onChange={(e) => setVoucherCodeInput(e.target.value)}
                disabled={!!appliedVoucher}
              />
            </div>
            {appliedVoucher ? (
              <button
                onClick={removeVoucher}
                className="px-3 bg-rose-50 text-rose-500 rounded-xl font-black text-[10px] uppercase tracking-widest border border-rose-100 transition-all hover:bg-rose-100 flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Hapus
              </button>
            ) : (
              <button
                onClick={handleApplyVoucher}
                disabled={!voucherCodeInput}
                className="px-4 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-giz-primary transition-all disabled:opacity-40"
              >
                Pakai
              </button>
            )}
          </div>
          {appliedVoucher && appliedVoucher.partnerName && (
            <div className="flex items-center gap-2 mt-2 p-2 bg-emerald-50 rounded-xl border border-emerald-100">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              <p className="text-[10px] font-bold text-emerald-700">
                Menggunakan voucher dari{" "}
                <span className="font-black">{appliedVoucher.partnerName}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2_Details = () => (
    <div className="space-y-4 md:space-y-5 animate-fadeIn">
      <div className="flex justify-between items-center border-b border-slate-100 pb-3">
        <h2 className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] md:tracking-[0.25em] text-slate-800 flex items-center gap-2">
          <User className="w-3.5 h-3.5 text-giz-primary" /> 02. Data Pengunjung
        </h2>
        <span className="inline-block text-[9px] font-black text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase tracking-widest">
          E-Ticket
        </span>
      </div>

      <div className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-3.5 space-y-2">
        {/* Service Fee breakdown jika ada */}
        {!priceCalculation.isInFreePeriod &&
          priceCalculation.serviceFee > 0 && (
            <div className="flex justify-between items-center text-[10px] border-b border-indigo-100/50 pb-2 mb-2">
              <div className="space-y-0.5">
                <p className="text-indigo-500">
                  Subtotal ({booking.quantity} tiket)
                </p>
                <p className="text-indigo-400 text-[9px]">
                  + Biaya Layanan ({priceCalculation.serviceFeePercent}%)
                </p>
              </div>
              <div className="text-right space-y-0.5">
                <p className="font-bold text-indigo-600">
                  {formatIDR(
                    priceCalculation.subTotal - priceCalculation.discount
                  )}
                </p>
                <p className="text-indigo-500 text-[9px]">
                  {formatIDR(priceCalculation.serviceFee)}
                </p>
              </div>
            </div>
          )}
        <div className="flex justify-between items-center">
          <div>
            <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">
              Total Bayar
            </p>
            <p className="text-base font-black text-indigo-900">
              {formatIDR(priceCalculation.total)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">
              Pengunjung
            </p>
            <p className="text-xs font-black text-indigo-900">
              {booking.quantity} Orang
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3 md:space-y-3.5">
        <div className="space-y-1">
          <label className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest ml-3">
            Nama Lengkap PIC
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Sesuai KTP"
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-100 rounded-xl bg-slate-50 text-slate-900 font-bold text-[11px] md:text-sm focus:border-giz-primary focus:bg-white outline-none transition-all"
              value={booking.customerName}
              onChange={(e) =>
                setBooking((prev) => ({
                  ...prev,
                  customerName: e.target.value,
                }))
              }
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest ml-3">
            Email Aktif
          </label>
          <div className="relative">
            <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="email"
              placeholder="budi@gmail.com"
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-100 rounded-xl bg-slate-50 text-slate-900 font-bold text-[11px] md:text-sm focus:border-giz-primary focus:bg-white outline-none transition-all"
              value={booking.customerEmail}
              onChange={(e) =>
                setBooking((prev) => ({
                  ...prev,
                  customerEmail: e.target.value,
                }))
              }
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest ml-3">
            Konfirmasi Email
          </label>
          <div className="relative">
            <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="email"
              placeholder="Ketik ulang email"
              className={`w-full pl-10 pr-4 py-2.5 border-2 rounded-xl bg-slate-50 text-slate-900 font-bold text-[11px] md:text-sm focus:border-giz-primary focus:bg-white outline-none transition-all ${
                confirmEmail && booking.customerEmail !== confirmEmail
                  ? "border-rose-200 bg-rose-50/30"
                  : "border-slate-100"
              }`}
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest ml-3">
            Nomor WhatsApp
          </label>
          <div className="relative">
            <Smartphone className="absolute left-4 top-[18px] -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="tel"
              placeholder="628..."
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-100 rounded-xl bg-slate-50 text-slate-900 font-bold text-[11px] md:text-sm focus:border-giz-primary focus:bg-white outline-none transition-all"
              value={booking.customerPhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
            />
          </div>
          <small
            className={`ml-3 text-[10px] ${
              /^628\d{7,17}$/.test(booking.customerPhone)
                ? "text-slate-400"
                : "text-red-500"
            }`}
          >
            * Nomor WhatsApp harus diawali dengan 628 (contoh: 628123456789)
          </small>
        </div>

        <div className="space-y-2">
          <label className="text-[8px] md:text-[10px] font-black text-slate-500 uppercase tracking-widest ml-3">
            Metode Pembayaran
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() =>
                setBooking((prev) => ({ ...prev, paymentMethod: "loket" }))
              }
              className={`p-2.5 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                booking.paymentMethod === "loket"
                  ? "border-giz-primary bg-indigo-50 shadow-md"
                  : "border-slate-100 bg-slate-50 hover:border-slate-200"
              }`}
            >
              <ShoppingBag
                className={`w-5 h-5 ${
                  booking.paymentMethod === "loket"
                    ? "text-giz-primary"
                    : "text-slate-400"
                }`}
              />
              <span
                className={`text-[10px] font-bold ${
                  booking.paymentMethod === "loket"
                    ? "text-giz-primary"
                    : "text-slate-500"
                }`}
              >
                LOKET (HARI H)
              </span>
            </button>
            <button
              type="button"
              disabled
              className="p-2.5 rounded-xl border-2 border-slate-100 bg-slate-50 flex flex-col items-center gap-2 opacity-50 cursor-not-allowed relative"
            >
              <Wallet className="w-5 h-5 text-slate-300" />
              <span className="text-[10px] font-bold text-slate-400">
                QRIS / VA ONLINE
              </span>
              <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[7px] font-black px-1.5 py-0.5 rounded-full">
                SOON
              </span>
            </button>
          </div>
          <p className="text-[9px] text-amber-600 font-medium bg-amber-50 p-2 rounded-lg mt-1.5">
            Tunjukkan kode booking ke petugas loket untuk pembayaran
          </p>
        </div>
      </div>

      <div className="p-3 md:p-3.5 bg-indigo-50/50 border-2 border-indigo-100/50 rounded-2xl flex items-start gap-3 shadow-sm">
        <input
          id="agree_check"
          type="checkbox"
          className="mt-0.5 w-5 h-5 rounded text-giz-primary focus:ring-giz-primary border-slate-300 cursor-pointer"
          checked={isAccepted}
          onChange={(e) => setIsAccepted(e.target.checked)}
        />
        <label
          htmlFor="agree_check"
          className="text-[9px] md:text-[10px] text-indigo-950 font-bold leading-relaxed cursor-pointer select-none"
        >
          Data benar & saya setuju{" "}
          <button type="button" className="text-indigo-600 underline font-bold">
            S&K
          </button>{" "}
          dan{" "}
          <button type="button" className="text-indigo-600 underline font-bold">
            Kebijakan Refund
          </button>
          .
        </label>
      </div>
    </div>
  );

  const renderStep3_Success = () => {
    const isLoket = booking.paymentMethod === "loket";
    const isPending = createdBooking?.status === "PENDING";
    const isCompleted =
      createdBooking?.status === "COMPLETED" ||
      createdBooking?.status === "PAID";

    if (isLoket && isPending) {
      return (
        <div className="animate-fadeIn space-y-6 md:space-y-7">
          <div className="space-y-5">
            <div className="text-center py-3">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                Booking Berhasil!
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                Silakan lakukan pembayaran di loket
              </p>
            </div>

            <div className="bg-slate-950 rounded-[2.3rem] p-6 text-white relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-1">
                      Kode Booking
                    </p>
                    <div className="flex items-center gap-3">
                      <h4 className="text-3xl font-black tracking-tighter text-amber-400 font-mono">
                        {bookingCode}
                      </h4>
                      <button
                        onClick={handleCopyCode}
                        className="p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                      >
                        {isCopied ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider animate-pulse">
                    Menunggu Bayar
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em] mb-1">
                      Tanggal Kunjungan
                    </p>
                    <p className="text-sm font-bold text-white/90">
                      {new Date(createdBooking?.date || "").toLocaleDateString(
                        "id-ID",
                        { day: "numeric", month: "long", year: "numeric" }
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em] mb-1">
                      Jumlah Tiket
                    </p>
                    <p className="text-sm font-bold text-white/90">
                      {createdBooking?.pax} pax
                    </p>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-white/10">
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] font-black text-white/40 uppercase tracking-[0.15em]">
                      Total Pembayaran
                    </p>
                    <p className="text-2xl font-black text-white">
                      {formatIDR(createdBooking?.totalPrice || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-slate-100 rounded-[2.3rem] p-6 text-center shadow-xl">
              <p className="text-[11px] font-black text-slate-900 uppercase tracking-widest mb-4">
                QR Code untuk Loket
              </p>
              <div className="bg-white p-3.5 rounded-3xl border border-slate-100 w-fit mx-auto shadow-inner mb-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=GIZPARK-${bookingCode}`}
                  alt="Ticket QR"
                  className="w-40 h-40 mix-blend-multiply"
                  loading="lazy"
                />
              </div>
              <p className="text-[9px] text-slate-400 font-bold mb-2">
                Tunjukkan QRCODE ini ke petugas loket.
              </p>
              <button
                disabled={isDownloading}
                className="w-full py-3.5 bg-slate-50 text-slate-900 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-slate-100 transition-all border border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={async () => {
                  setIsDownloading(true);
                  try {
                    const response = await fetch(
                      `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=GIZPARK-${bookingCode}`
                    );
                    const qrBlob = await response.blob();
                    const qrUrl = window.URL.createObjectURL(qrBlob);

                    const qrImg = new Image();
                    qrImg.crossOrigin = "anonymous";

                    qrImg.onload = () => {
                      const canvas = document.createElement("canvas");
                      const ctx = canvas.getContext("2d");
                      if (!ctx) {
                        setIsDownloading(false);
                        return;
                      }

                      const padding = 40;
                      const qrSize = 300;
                      const infoHeight = 180;
                      canvas.width = qrSize + padding * 2;
                      canvas.height = qrSize + infoHeight + padding * 2;

                      ctx.fillStyle = "#FFFFFF";
                      ctx.fillRect(0, 0, canvas.width, canvas.height);

                      ctx.fillStyle = "#4F46E5";
                      ctx.fillRect(0, 0, canvas.width, 50);

                      ctx.fillStyle = "#FFFFFF";
                      ctx.font = "bold 18px Arial";
                      ctx.textAlign = "center";
                      ctx.fillText(
                        "E-TIKET GIZ & RIW - KBS",
                        canvas.width / 2,
                        32
                      );

                      ctx.drawImage(qrImg, padding, 70, qrSize, qrSize);

                      const infoY = 70 + qrSize + 20;

                      ctx.fillStyle = "#1E293B";
                      ctx.font = "bold 14px Arial";
                      ctx.textAlign = "center";
                      ctx.fillText("KODE BOOKING", canvas.width / 2, infoY);

                      ctx.fillStyle = "#4F46E5";
                      ctx.font = "bold 24px monospace";
                      ctx.fillText(bookingCode, canvas.width / 2, infoY + 30);

                      ctx.fillStyle = "#64748B";
                      ctx.font = "12px Arial";
                      ctx.fillText(
                        `${createdBooking?.pax || booking.quantity} Pax • ${
                          createdBooking?.ticketType ||
                          selectedTicketDetails?.title ||
                          ""
                        }`,
                        canvas.width / 2,
                        infoY + 55
                      );

                      ctx.fillText(
                        `Kunjungan: ${new Date(
                          createdBooking?.date || booking.selectedDate
                        ).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}`,
                        canvas.width / 2,
                        infoY + 75
                      );

                      ctx.fillStyle = "#F59E0B";
                      ctx.font = "bold 11px Arial";
                      ctx.fillText(
                        "⚠ Tunjukan tiket ini di loket.",
                        canvas.width / 2,
                        infoY + 100
                      );

                      canvas.toBlob((blob) => {
                        if (!blob) {
                          setIsDownloading(false);
                          return;
                        }
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `tiket-${bookingCode}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        window.URL.revokeObjectURL(qrUrl);
                        setIsDownloading(false);
                      }, "image/png");
                    };

                    qrImg.onerror = () => {
                      setIsDownloading(false);
                      Swal.fire({
                        icon: "error",
                        title: "Gagal",
                        text: "Gagal memuat QR Code. Silakan coba lagi.",
                        confirmButtonColor: "#4F46E5",
                      });
                    };

                    qrImg.src = qrUrl;
                  } catch (error) {
                    setIsDownloading(false);
                    Swal.fire({
                      icon: "error",
                      title: "Gagal",
                      text: "Gagal mengunduh E-Ticket. Silakan coba lagi.",
                      confirmButtonColor: "#4F46E5",
                    });
                  }
                }}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Memproses...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" /> Simpan Ke Galeri
                  </>
                )}
              </button>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-3.5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-[11px] font-bold text-amber-800">
                    Petunjuk Pembayaran di Loket:
                  </p>
                  <ol className="text-[10px] text-amber-700 space-y-1 list-decimal ml-4">
                    <li>Download QRCODE atau catat kode booking</li>
                    <li>Tunjukkan ke petugas loket</li>
                    <li>Lakukan pembayaran sesuai jumlah yang tertera</li>
                    <li>Tiket otomatis aktif setelah dikonfirmasi</li>
                  </ol>
                </div>
              </div>
            </div>

            <button
              onClick={resetForm}
              className="mt-5 w-full py-3.5 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-xl hidden md:block"
            >
              Beli Tiket Lainnya
            </button>
          </div>
        </div>
      );
    }

    if (!isLoket && isPending) {
      return (
        <div className="animate-fadeIn space-y-6 md:space-y-7">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <PaymentGateway
              booking={createdBooking!}
              onSuccess={handlePaymentSuccess}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="animate-fadeIn space-y-6 md:space-y-7">
        <div className="space-y-5">
          <div className="text-center py-3">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
              {isLoket ? "Check-In Berhasil!" : "Pembayaran Berhasil!"}
            </h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              E-Ticket telah aktif dan siap digunakan
            </p>
          </div>

          <div className="bg-slate-950 rounded-[2.3rem] p-6 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em] mb-1">
                    Kode Booking
                  </p>
                  <div className="flex items-center gap-3">
                    <h4 className="text-3xl font-black tracking-tighter text-indigo-400 font-mono">
                      {bookingCode}
                    </h4>
                    <button
                      onClick={handleCopyCode}
                      className="p-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                    >
                      {isCopied ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="bg-emerald-500 text-white text-[9px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow-lg shadow-emerald-500/20">
                  {createdBooking?.status === "COMPLETED"
                    ? "Completed"
                    : "Paid"}
                </div>
              </div>

              <div className="space-y-3.5 border-t border-white/5 pt-5">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                    Item Wahana
                  </span>
                  <span className="text-xs font-bold text-white uppercase">
                    {createdBooking?.ticketType || selectedTicketDetails?.title}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                    Tanggal Kunjungan
                  </span>
                  <span className="text-xs font-bold text-white">
                    {new Date(createdBooking?.date || "").toLocaleDateString(
                      "id-ID",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                    Jumlah
                  </span>
                  <span className="text-xs font-bold text-white">
                    {createdBooking?.pax} Pax
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
                    Total Bayar
                  </span>
                  <span className="text-sm font-black text-indigo-400">
                    {formatIDR(
                      createdBooking?.totalPrice || priceCalculation.total
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-slate-100 rounded-[2.3rem] p-6 text-center shadow-xl">
            <p className="text-[11px] font-black text-slate-900 uppercase tracking-widest mb-6">
              QR Code Ticket Masuk
            </p>
            <div className="bg-white p-3.5 rounded-3xl border border-slate-100 w-fit mx-auto shadow-inner mb-5">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=GIZPARK-${bookingCode}`}
                alt="Ticket QR"
                className="w-40 h-40 mix-blend-multiply"
                loading="lazy"
              />
            </div>
          </div>

          <button
            onClick={resetForm}
            className="mt-5 w-full py-3.5 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-xl hidden md:block"
          >
            Beli Tiket Lainnya
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`bg-white rounded-2xl md:rounded-[3.5rem] shadow-2xl border border-slate-200/50 overflow-hidden sticky top-20 md:top-28 transition-all duration-500 z-[40] ${
        activeCategory === "rombongan"
          ? "opacity-40 grayscale cursor-pointer"
          : "opacity-100"
      }`}
      onClick={() => {
        if (activeCategory === "rombongan" && onActivate) onActivate();
      }}
    >
      <div className="bg-slate-900 p-5 md:p-7 text-white flex justify-between items-center border-b border-white/5 relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-[11px] md:text-[14px] font-black uppercase tracking-[0.3em] md:tracking-[0.35em] mb-0.5 md:mb-1">
            Personal Booking
          </h2>
          <p className="text-[8px] md:text-[9px] text-slate-400 font-black uppercase tracking-widest opacity-80 flex items-center gap-1.5">
            <ShieldCheck className="w-2.5 h-2.5 md:w-3 h-3 text-emerald-500" />{" "}
            Secure Payment
          </p>
        </div>
        <div className="bg-white/10 p-2 md:p-3.5 rounded-xl md:rounded-[1.2rem] backdrop-blur-md relative z-10 border border-white/10">
          <CreditCard
            className="w-4 h-4 md:w-6 md:h-6 text-indigo-300"
            aria-hidden="true"
          />
        </div>
      </div>

      <div
        className={`p-4 md:p-8 ${
          activeCategory === "rombongan" ? "pointer-events-none" : ""
        }`}
      >
        {booking.step < 3 && (
          <div className="flex items-center mb-5 md:mb-6 relative px-3 md:px-8">
            <div
              className={`w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-black z-10 transition-all duration-700 ${
                booking.step >= 1
                  ? "bg-giz-primary text-white scale-110 shadow-xl"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              1
            </div>
            <div
              className={`h-1 flex-1 -mx-1 transition-colors duration-1000 ${
                booking.step >= 2 ? "bg-giz-primary" : "bg-slate-100"
              }`}
            ></div>
            <div
              className={`w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-black z-10 transition-all duration-700 ${
                booking.step >= 2
                  ? "bg-giz-primary text-white scale-110 shadow-xl"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              2
            </div>
            <div
              className={`h-1 flex-1 -mx-1 transition-colors duration-1000 ${
                booking.step >= 3 ? "bg-giz-primary" : "bg-slate-100"
              }`}
            ></div>
            <div
              className={`w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center text-[9px] md:text-[10px] font-black z-10 transition-all duration-700 ${
                booking.step >= 3
                  ? "bg-giz-primary text-white scale-110 shadow-xl"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              3
            </div>
          </div>
        )}

        {booking.step === 1 && renderStep1_Ticket()}
        {booking.step === 2 && renderStep2_Details()}
        {booking.step === 3 && renderStep3_Success()}

        {booking.step < 3 && (
          <div className="mt-3 md:mt-3 hidden md:block">
            <div className="flex justify-end mb-5">
              {booking.step > 1 && (
                <button
                  onClick={handleBack}
                  className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] bg-indigo-50 px-5 py-2.5 rounded-2xl hover:bg-indigo-100 transition-all"
                >
                  Kembali
                </button>
              )}
            </div>
            <button
              onClick={handleNext}
              disabled={isNextDisabled || (booking.step === 2 && !isAccepted)}
              className={`w-full py-5 rounded-[1.6rem] font-black text-white text-[12px] uppercase tracking-[0.3em] shadow-2xl transition-all active:scale-95 disabled:opacity-50 hover:-translate-y-1 ${
                isNextDisabled || (booking.step === 2 && !isAccepted)
                  ? "bg-slate-300"
                  : "bg-slate-900 hover:bg-giz-primary"
              }`}
            >
              {booking.step === 1 ? "Lanjut Isi Data" : "Konfirmasi & Bayar"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
